import { useTranslate } from '@tolgee/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Drawer,
  DrawerProps,
  Form,
  InputNumber,
  message,
  Modal,
  Select,
} from 'antd';
import { intersection, uniq } from 'lodash-es';
import useModal from 'hooks/useModal';
import {
  calculateCurrency,
  CalculateRes,
  createReturn,
  getTradeEligibilityList,
  logisticsList,
  tradeList,
} from 'services/api/conversation';
import { useDebounceEffect } from 'ahooks';
import { CloseOutlined } from '@ant-design/icons';
import { formatNumber } from 'utils/numberFormat';
import { ProductImages } from '../ProductImages';
import { SimpleInputNumber } from '../ToDoBundle/FormItem';
import styled from '@emotion/styled';

function RefundDrawer({
  type,
  refundData,
  order,
  storeId,
  onUpdate,
  ...otherProps
}: {
  type: 'REFUND' | 'RETURN_AND_REFUND';
  refundData: Conversation.Trade.SkuEligibility[];
  order: Conversation.Trade.TradeWithLogistics;
  storeId: string | number;
  onUpdate: (trade: Conversation.Trade.TradeWithLogistics) => void;
} & DrawerProps) {
  const { internalTradeId, items, unit, platform, internalBuyerId, externalTradeId } = order;

  const { t } = useTranslate();

  const ids = useMemo(() => {
    return refundData.reduce<string[]>((res, { lineItemEligibility }) => {
      return lineItemEligibility.reduce((res, { orderLineItemIds }) => {
        return [...res, ...orderLineItemIds];
      }, res);
    }, []);
  }, [refundData]);

  const [selectedIds, setSelectedIds] = useState<string[]>(ids);

  const [selectedReason, setSelectedReason] = useState<string>();

  const reasonOptions = useMemo(() => {
    return (
      refundData.reduce<string[] | undefined>((res, { lineItemEligibility }) => {
        return lineItemEligibility.reduce((res, { availableReasonNames, orderLineItemIds }) => {
          if (orderLineItemIds.some((id) => selectedIds.includes(id))) {
            return res ? intersection(res, availableReasonNames) : availableReasonNames;
          }
          return res;
        }, res);
      }, undefined) || []
    );
  }, [refundData, selectedIds]);

  useEffect(() => {
    if (selectedReason && !reasonOptions.includes(selectedReason)) {
      setSelectedReason(undefined);
    }
  }, [selectedReason, reasonOptions]);

  const [shipmentType, setShipmentType] = useState<'PLATFORM' | 'BUYER_ARRANGE'>();

  const [handoverMethod, setHandoverMethod] = useState<'DROP_OFF' | 'PICKUP'>();

  const [loading, setLoading] = useState(false);
  const [calculateRes, setCalculateRes] = useState<CalculateRes>();

  const [refundTotal, setRefundTotal] = useState<number>();

  const calculateRef = useRef(0);

  useDebounceEffect(() => {
    if (selectedReason && selectedIds.length) {
      setLoading(true);
      calculateRef.current += 1;
      const currentCalculate = calculateRef.current;
      calculateCurrency({
        internalTradeId,
        requestType: type,
        reasonName: selectedReason,
        orderLineItemIds: selectedIds,
        shipmentType,
        handoverMethod,
      })
        .then((res) => {
          if (calculateRef.current === currentCalculate) {
            setLoading(false);
            setCalculateRes(res);
            setRefundTotal(Number(res.refundTotal));
          }
        })
        .catch(() => {
          if (calculateRef.current === currentCalculate) {
            setLoading(false);
            setCalculateRes(undefined);
          }
        });
    } else {
      setCalculateRes(undefined);
    }
  }, [type, selectedReason, selectedIds, shipmentType, handoverMethod]);

  useEffect(() => {
    return () => {
      setSelectedReason(undefined);
      setSelectedIds(ids);
      setShipmentType(undefined);
      setHandoverMethod(undefined);
      setCalculateRes(undefined);
      setRefundTotal(undefined);
      calculateRef.current = 0;
    };
  }, [refundData, ids]);

  const formatter = (num: number | string) => `${unit} ${formatNumber(num, '0,0.00')}`;

  const title = t(type === 'REFUND' ? 'REFUND' : '退货退款');

  const { allChecked, indeterminate, skusData } = useMemo(() => {
    const allChecked = ids.every((id) => selectedIds.includes(id));
    const indeterminate = !allChecked && ids.some((id) => selectedIds.includes(id));

    const skusData = refundData.map(({ skuId, lineItemEligibility }) => {
      const item = items.find(({ externalSkuId }) => externalSkuId === skuId);
      return {
        skuId,
        item,
        lineItemEligibility: lineItemEligibility.map((cur) => {
          const { orderLineItemIds } = cur;
          const selected = orderLineItemIds.filter((id) => selectedIds.includes(id));

          return {
            ...cur,
            selected,
          };
        }),
      };
    });

    return {
      allChecked,
      indeterminate,
      skusData,
    };
  }, [ids, refundData, items, selectedIds]);

  const help = !calculateRes
    ? undefined
    : refundTotal === undefined
    ? t('trade.可退金额必填')
    : refundTotal <= 0
    ? t('trade.超出最小可退金额')
    : refundTotal > Number(calculateRes?.refundTotal)
    ? t('trade.超出最大可退金额')
    : undefined;

  return (
    <Drawer width={888} closable={false} title={false} {...otherProps}>
      <Container>
        <header>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={otherProps.onClose as any}
          />
          <h2>{title}</h2>
        </header>
        <div>
          <section>
            <div>
              <h3>{t('x个商品', { count: ids.length })}</h3>
              <Checkbox
                checked={allChecked}
                indeterminate={indeterminate}
                onChange={({ target: { checked } }) => setSelectedIds(checked ? ids : [])}
              >
                {t('全选')}
              </Checkbox>
              <ul>
                {skusData.map(({ item, lineItemEligibility }) => {
                  if (!item) {
                    return null;
                  }
                  const { itemPic, itemName, sellerSku, unitPrice } = item;
                  return lineItemEligibility.map(({ selected, orderLineItemIds }) => {
                    return (
                      <li>
                        <div>
                          <ProductImages images={[itemPic]} platform={platform} />
                          <section>
                            <h4>{itemName}</h4>
                            <p>{`${t('SKU货号')}: ${sellerSku || '-'}`}</p>
                          </section>
                          <span>
                            <section>
                              {formatter(unitPrice)}
                              <span>x{orderLineItemIds.length}</span>
                            </section>
                            <SimpleInputNumber
                              min={0}
                              max={orderLineItemIds.length}
                              value={selected.length}
                              onChange={(value) =>
                                setSelectedIds(
                                  uniq([
                                    ...selectedIds.filter((id) => !orderLineItemIds.includes(id)),
                                    ...orderLineItemIds.slice(0, value),
                                  ]),
                                )
                              }
                            />
                          </span>
                        </div>
                      </li>
                    );
                  });
                })}
              </ul>
            </div>
            <div>
              <h3>{t('退款原因')}</h3>
              <Select
                placeholder={t('请选择')}
                value={selectedReason}
                onChange={setSelectedReason}
                options={reasonOptions.map((reason) => {
                  return {
                    value: reason,
                    label: t(`tiktok.returnReason.${reason}`),
                  };
                })}
              />
              <span>{t('Return or refund reason only visible to customer and you')}</span>
            </div>
            {type === 'RETURN_AND_REFUND' && (
              <div>
                <h3>{t('寄件与退货方式')}</h3>
                <section>
                  <Select
                    placeholder={t('寄件方式')}
                    value={handoverMethod}
                    onChange={setHandoverMethod}
                    options={[
                      { value: 'DROP_OFF', label: t('自行寄回') },
                      { value: 'PICKUP', label: t('上门取件') },
                    ]}
                  />
                  <Select
                    placeholder={t('退货方式')}
                    value={shipmentType}
                    onChange={setShipmentType}
                    options={[
                      { value: 'PLATFORM', label: t('平台物流') },
                      { value: 'BUYER_ARRANGE', label: t('买家自寄') },
                    ]}
                  />
                </section>
              </div>
            )}
          </section>
          <div>
            <h3>{t('Summary')}</h3>
            {calculateRes && (
              <ul>
                <li>
                  <div>{t('Product refund')}</div>
                  <span>{formatter(calculateRes.refundTotal)}</span>
                </li>
                <li>
                  <div>{t('Shipping fee refund')}</div>
                  <span>{formatter(calculateRes.refundShippingFee)}</span>
                </li>
                {type === 'RETURN_AND_REFUND' && (
                  <li>
                    <div>{t('Return shipping fee')}</div>
                    <span>{formatter(calculateRes.retailDeliveryFee)}</span>
                  </li>
                )}
                <li>
                  <div>{t('Tax refund')}</div>
                  <span>{formatter(calculateRes.refundTax)}</span>
                </li>
              </ul>
            )}
            <dl>
              <dt>{t('ViewRefund.退款总金额')}</dt>
              <dd>
                <Form.Item
                  validateStatus={help ? 'error' : ''}
                  help={help || t('x可用于退款', { x: formatter(calculateRes?.refundTotal || 0) })}
                >
                  <InputNumber
                    disabled={!calculateRes?.modifyTheAmount}
                    prefix={unit}
                    value={refundTotal}
                    onChange={(value) => setRefundTotal(value ?? undefined)}
                    precision={2}
                  />
                </Form.Item>
              </dd>
            </dl>
            <Button
              block
              loading={loading}
              disabled={
                Boolean(help) ||
                !calculateRes ||
                !refundTotal ||
                !selectedIds.length ||
                !selectedReason
              }
              type="primary"
              onClick={() => {
                Modal.confirm({
                  title: t('确认退款x ？', { x: formatter(refundTotal || 0) }),
                  onOk: () => {
                    if (!calculateRes || !selectedReason) {
                      return;
                    }
                    setLoading(true);
                    createReturn({
                      internalTradeId,
                      orderLineItemIds: selectedIds,
                      returnReason: selectedReason,
                      requestType: type,
                      currency: calculateRes.currency,
                      refundTotal: String(refundTotal),
                      shipmentType,
                      handoverMethod,
                    })
                      .then(() => {
                        message.success(t('操作成功'));
                        otherProps.onClose?.({} as any);

                        setTimeout(() => {
                          tradeList({
                            page_no: 1,
                            page_size: 1,
                            internal_buyer_id: internalBuyerId,
                            internal_store_id: storeId,
                            external_trade_ids: externalTradeId,
                          })
                            .then((res) => {
                              const { tradeList: [trade] = [] } = res;

                              if (!trade) {
                                return;
                              }

                              return logisticsList({
                                internalStoreId: storeId,
                                internal_trade_ids: internalTradeId,
                                needPackageData: true,
                              }).then((res) => {
                                return getTradeEligibilityList({
                                  internalTradeIds: [internalTradeId],
                                }).then(({ orderEligibilityList }) => {
                                  onUpdate({
                                    ...trade,
                                    logisticsList: res.data.data,
                                    eligibility: orderEligibilityList[0],
                                    ['_queryParams' as any]: (order as any)._queryParams,
                                  });
                                });
                              });
                            })
                            .catch(() => {});
                        }, 2000);
                      })
                      .catch(() => {
                        message.error(t('translation.操作失败，请稍后再试'));
                      })
                      .finally(() => {
                        setLoading(false);
                      });
                  },
                });
              }}
            >
              {title}
            </Button>
          </div>
        </div>
      </Container>
    </Drawer>
  );
}

function Refund({
  type,
  order,
  storeId,
  onUpdate,
}: {
  type: 'REFUND' | 'RETURN_AND_REFUND';
  order: Conversation.Trade.TradeWithLogistics;
  storeId: string | number;
  onUpdate: (trade: Conversation.Trade.TradeWithLogistics) => void;
}) {
  const { eligibility: { skuEligibility } = {} } = order;

  const { t } = useTranslate();

  const refundData = useMemo(() => {
    return (skuEligibility || [])
      .map(({ skuId, lineItemEligibility }) => {
        return {
          skuId,
          lineItemEligibility: lineItemEligibility.filter(
            ({ requestType }) => requestType === type,
          ),
        };
      })
      .filter((_) => _.lineItemEligibility.length);
  }, [skuEligibility, type]);

  const { visible, show, hide } = useModal();

  if (!refundData.length) {
    return <></>;
  }

  const drawer = (
    <RefundDrawer
      type={type}
      refundData={refundData}
      order={order}
      storeId={storeId}
      onUpdate={onUpdate}
      visible={visible}
      onClose={hide}
    />
  );

  const button = (
    <Button type="primary" size="small" onClick={show}>
      {t(type === 'REFUND' ? 'REFUND' : '退货退款')}
    </Button>
  );

  return (
    <>
      {button}
      {drawer}
    </>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100% + 48px);
  margin: -24px;
  padding: 16px;
  background: #f7f9fa;
  > header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    > button {
      margin-right: 8px;
      background: #f0f2f5;
    }
    > h2 {
      margin-bottom: 0;
    }
  }
  > div {
    flex: 1 0 0;
    display: flex;
    align-items: flex-start;
    overflow: hidden;
    > section {
      flex: 1 0 0;
      max-height: 100%;
      overflow: auto;
      > div {
        margin-bottom: 16px;
        padding: 16px;
        background: #fff;
        border-radius: 4px;
        > label {
          margin-bottom: 12px;
        }
        > ul {
          margin: 0;
          padding: 0;
          list-style: none;
          > li {
            background: #f7f9fa;
            &:not(:last-of-type) {
              margin-bottom: 16px;
            }
            > label {
              display: flex;
              width: 100%;
              padding: 8px 12px;
              border-bottom: solid 1px #ededed;
            }
            > div {
              display: flex;
              padding: 12px;
              border-radius: 4px;
              > div {
                margin-right: 8px;
              }
              > section {
                flex: 1 0 0;
                overflow: hidden;
                > h4 {
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  margin-bottom: 4px;
                }
                > p {
                  margin-bottom: 0;
                  font-size: 12px;
                  color: #84868c;
                }
              }
              > span {
                flex: 0 0 auto;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                align-items: flex-end;
                gap: 8px;
                margin-left: 6px;
                > section {
                  display: flex;
                  flex-direction: column;
                  align-items: flex-end;
                }
              }
            }
          }
        }
        > .ant-select {
          width: 100%;
        }
        > span {
          display: block;
          margin-top: 6px;
          font-size: 12px;
          color: #84868c;
        }
        > section {
          display: flex;
          gap: 16px;
          > * {
            flex: 1 0 0;
          }
        }
      }
    }
    > div {
      flex: 0 0 auto;
      width: 252px;
      max-height: 100%;
      margin-left: 16px;
      padding: 14px 16px;
      border-radius: 4px;
      background: #fff;
      overflow: auto;
      > ul {
        margin: 0;
        padding: 0;
        list-style: none;
        > li {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          > div {
            flex: 1 0 0;
          }
          > span {
            flex: 0 0 auto;
          }
        }
      }
      > dl {
        > dt {
          margin-bottom: 8px;
        }
        > dd {
          display: flex;
          flex-direction: column;
          .ant-input-number-affix-wrapper {
            width: 100%;
          }
          .ant-form-item-explain {
            margin-top: 8px;
          }
        }
      }
      > div {
        margin-bottom: 12px;
        color: #84868c;
      }
    }
  }
`;

export default Refund;
